/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
/* eslint-disable react/no-multi-comp */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { ChartsTooltipContainer, useItemTooltip } from '@mui/x-charts/ChartsTooltip';
import { useDrawingArea } from '@mui/x-charts/hooks';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import {
  formatBRL,
  getCategoriaChartColor,
  resolveTipoNotaDef,
} from './constants';
import { CHART_HEIGHT } from './chartShared';

const PIE_MARGIN = { left: 8, right: 120, top: 8, bottom: 8 };

const PieEmpty = ({ message }) => (
  <Box
    sx={{
      height: CHART_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      gap: 1,
    }}
  >
    <PieChartOutlineIcon sx={{ fontSize: 40, opacity: 0.4 }} />
    <Typography variant="body2">{message}</Typography>
  </Box>
);

PieEmpty.propTypes = {
  message: PropTypes.string.isRequired,
};

const PieCenterLabel = ({ title, value }) => {
  const theme = useTheme();
  const { width, height, left, top } = useDrawingArea();
  const cx = left + width / 2;
  const cy = top + height / 2;
  const valueText = String(value ?? '');
  const valueFontSize = valueText.length > 16 ? 11 : valueText.length > 12 ? 12 : 14;

  return (
    <g pointerEvents="none" aria-hidden="true">
      <text
        x={cx}
        y={cy - 10}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={theme.palette.text.secondary}
        style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4 }}
      >
        {title}
      </text>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={theme.palette.text.primary}
        style={{ fontSize: valueFontSize, fontWeight: 700 }}
      >
        {value}
      </text>
    </g>
  );
};

PieCenterLabel.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const formatPercent = (value) =>
  value != null
    ? `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    : null;

const DistribuicaoTooltipContent = ({ pieData }) => {
  const item = useItemTooltip();
  if (!item) return null;

  const dataIndex = item.identifier?.dataIndex;
  const row = dataIndex != null ? pieData[dataIndex] : null;
  const categoria = row?.categoria ?? item.label ?? '—';
  const valor = formatBRL(item.value ?? row?.value);
  const percentual = formatPercent(row?.percentual);

  return (
    <ChartsTooltipContainer trigger="item">
      <Paper elevation={4} sx={{ px: 1.5, py: 1, minWidth: 160, borderRadius: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {categoria}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {valor}
          </Typography>
          {percentual && (
            <Typography variant="caption" color="text.secondary">
              ({percentual})
            </Typography>
          )}
        </Stack>
      </Paper>
    </ChartsTooltipContainer>
  );
};

DistribuicaoTooltipContent.propTypes = {
  pieData: PropTypes.array.isRequired,
};

const DistribuicaoChart = ({ items, loading, totalGasto, height, onSelectCategoria }) => {
  const chartHeight = height || CHART_HEIGHT;

  const pieData = useMemo(
    () =>
      (items || [])
        .filter((item) => Number(item.valor) > 0)
        .map((item, index) => {
          const valor = Number(item.valor) || 0;
          const def = resolveTipoNotaDef(item.tipoNota, item.categoria);
          const categoria = def.label;
          const percentual = item.percentual != null ? Number(item.percentual) : null;
          const brl = formatBRL(valor);
          const pct = formatPercent(percentual);
          return {
            id: def.enum || `cat-${index}`,
            value: valor,
            categoria,
            tipoNota: def.enum,
            label: (location) => {
              if (location === 'legend') {
                return pct ? `${categoria} — ${brl} (${pct})` : `${categoria} — ${brl}`;
              }
              return categoria;
            },
            percentual,
            color: getCategoriaChartColor(item.categoria, item.tipoNota),
          };
        }),
    [items],
  );

  const pieColors = pieData.map((item) => item.color);
  const hasData = pieData.length > 0;

  const TooltipSlot = useMemo(
    () => function DistribuicaoTooltipSlot() {
      return <DistribuicaoTooltipContent pieData={pieData} />;
    },
    [pieData],
  );

  const series = useMemo(
    () => [
      {
        data: pieData,
        innerRadius: 50,
        paddingAngle: 2,
        cornerRadius: 4,
        highlightScope: { fade: 'global', highlight: 'item' },
        valueFormatter: (value) => formatBRL(value),
      },
    ],
    [pieData],
  );

  if (loading) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  if (!hasData) {
    return <PieEmpty message="Sem gastos no período selecionado" />;
  }

  return (
    <Box
      sx={{ width: '100%', display: 'flex', justifyContent: 'center', overflow: 'visible' }}
      aria-label="Gráfico de distribuição de gastos por categoria"
    >
      <PieChart
        series={series}
        colors={pieColors}
        height={chartHeight}
        margin={PIE_MARGIN}
        slots={{ tooltip: TooltipSlot }}
        onItemClick={
          onSelectCategoria
            ? (_event, identifier) => {
              const row = pieData[identifier?.dataIndex];
              if (row?.categoria) onSelectCategoria(row.categoria);
            }
            : undefined
        }
        slotProps={{
          legend: {
            position: { vertical: 'middle', horizontal: 'right' },
          },
        }}
        sx={{
          overflow: 'visible',
          cursor: onSelectCategoria ? 'pointer' : 'default',
        }}
      >
        {totalGasto > 0 && (
          <PieCenterLabel title="Total" value={formatBRL(totalGasto)} />
        )}
      </PieChart>
    </Box>
  );
};

DistribuicaoChart.propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  totalGasto: PropTypes.number,
  height: PropTypes.number,
  onSelectCategoria: PropTypes.func,
};

DistribuicaoChart.defaultProps = {
  items: [],
  loading: false,
  totalGasto: 0,
  height: null,
  onSelectCategoria: null,
};

export default DistribuicaoChart;
